import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Eye, Loader2 } from "lucide-react";
import { MyTrainingForms } from "@/components/MyTrainingForms";

export const CAT_LABEL = { contract: "Contract", policy: "Policy", certificate: "Certificate", right_to_work: "Right to work",
  training: "Training", disciplinary: "Disciplinary", medical: "Medical", other: "Other", restricted: "Restricted" };

export const ExpiryBadge = ({ d }) => {
  if (!d.expires_at) return <span className="text-xs text-slate-400">No expiry</span>;
  const cls = d.expiry_state === "expired" ? "bg-rose-50 text-rose-700" : d.expiry_state === "expiring" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={`hr-expiry-${d.id}`}>{d.expiry_state === "expired" ? "Expired" : "Expires"} {d.expires_at}</span>;
};

export const openHrFile = async (id, download, version = 0, sensitive = false) => {
  let purpose;
  if (sensitive) {
    purpose = window.prompt("This is a sensitive document. Briefly state why you need to open it (recorded in the HR audit trail):");
    if (!purpose || purpose.trim().length < 5) { toast.error("A purpose of at least 5 characters is required"); return; }
  }
  try {
    const r = await api.get(`/hr/documents/${id}/file`, { params: { download, version: version || undefined, purpose } });
    window.open(r.data.url, "_blank", "noopener");
  } catch (e) { toast.error(e.response?.data?.detail || "File not available"); }
};

export function MyHrDocuments() {
  const [docs, setDocs] = useState(null);
  const load = useCallback(() => { api.get("/hr/me/documents").then((r) => setDocs(r.data.documents)).catch(() => setDocs([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="my-hr-documents">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><FileText className="h-4 w-4 text-indigo-500" />My documents</h2>
      <p className="mt-0.5 text-xs text-slate-500">Contracts, policies, certificates and training records.</p>
      <MyTrainingForms />
      {docs === null ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" /> : docs.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="my-hr-documents-empty">No documents have been shared with you yet.</div>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`my-hr-doc-${d.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-800">{d.title}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">{CAT_LABEL[d.category]} · v{d.version} <ExpiryBadge d={d} />{!d.downloadable && <span className="text-amber-600" data-testid={`my-hr-scanning-${d.id}`}>Security scan in progress</span>}</div>
              </div>
              <div className="flex gap-2">
                <button disabled={!d.downloadable} onClick={() => openHrFile(d.id, false)} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40" data-testid={`my-hr-view-${d.id}`}><Eye className="h-3.5 w-3.5" />View</button>
                <button disabled={!d.downloadable} onClick={() => openHrFile(d.id, true)} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40" data-testid={`my-hr-download-${d.id}`}><Download className="h-3.5 w-3.5" />Download</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
